import React, {
  useState,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useCallback,
} from "react";
import {
  Table,
  Card,
  Container,
  TableBody,
  TableContainer,
  IconButton,
  Box,
  useTheme,
  Typography,
  TableRow,
  Grid,
} from "@mui/material";
import {
  TABLE_HEAD_PORTFOLIO_SHORT,
  TABLE_HEAD_PORTFOLIO_LONG,
  applySortFilter,
  getComparator,
} from "../../component/Methods";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import PositionTable from "./PositionTable";
import PositionPopUp from "./Popup/PositionPopUp";
import VantageListHead from "../../component/ListHead/VantageListHead";
import {
  POSITION_ROW_HEIGHT,
  POSITION_VIRTUAL_BUFFER,
  getPositionTableBodyHeight,
} from "./styles/positionTableConstants";

const ROW_HEIGHT = POSITION_ROW_HEIGHT;
const BUFFER_SIZE = POSITION_VIRTUAL_BUFFER;

const AssetPositions = React.memo((props) => {
  const { data, fundVal, StrategeyVal, onExportDataChange } = props;
  const [long, setlong] = useState([]);
  const [expandLong, setExpandLong] = useState([]);
  const [short, setshort] = useState([]);
  const [expandShort, setExpandShort] = useState([]);
  const [orderByShort, setOrderByShort] = useState("size");
  const [orderByLong, setOrderByLong] = useState("size");
  const [longSortOrder, setLongSortOrder] = useState("desc");
  const [longFilterName, setLongFilterName] = useState("");
  const [shortSortOrder, setShortSortOrder] = useState("desc");
  const [shortFilterName, setShortFilterName] = useState("");
  const [isGridView, setIsGridView] = useState(false);
  const [isGridViewShort, setIsGridViewShort] = useState(false);

  // Virtual scrolling refs
  const longTableRef = useRef(null);
  const shortTableRef = useRef(null);
  const [longScrollTop, setLongScrollTop] = useState(0);
  const [shortScrollTop, setShortScrollTop] = useState(0);
  const [longContainerHeight, setLongContainerHeight] = useState(0);
  const [shortContainerHeight, setShortContainerHeight] = useState(0);

  const measureContainer = useCallback((node, setHeight) => {
    if (!node) return undefined;
    const updateHeight = () => {
      setHeight(node.clientHeight);
    };
    updateHeight();
    const observer = new ResizeObserver(updateHeight);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  useLayoutEffect(() => {
    return measureContainer(longTableRef.current, setLongContainerHeight);
  }, [long.length, measureContainer]);

  useLayoutEffect(() => {
    return measureContainer(shortTableRef.current, setShortContainerHeight);
  }, [short.length, measureContainer]);

  // Memoize the data processing to prevent unnecessary recalculations
  const processedData = useMemo(() => {
    const filteredData = data;

    // Table side follows today's share count (includes filled/all trades).
    // Overnight count is only used when today is flat, so a closed row stays put.
    const todayShares = (item) => Number(item?.deltaAdjustedShareCount);
    const overnightShares = (item) =>
      Number(
        item?.deltaAdjustedShareCountWithoutTrades ??
          item?.deltaAdjustedShareCount
      );
    const isLongSide = (item) => {
      const today = todayShares(item);
      if (today > 0) return true;
      if (today < 0) return false;
      return overnightShares(item) > 0;
    };
    const long = filteredData?.filter((i) => isLongSide(i));
    const short = filteredData?.filter((i) => !isLongSide(i));

    const renamedShort = short
      ?.map((item) => ({
        short_size: item?.size,
        short_sizeWithoutTrades: item?.sizeWithoutTrades,
        short_current: item?.sharePrice,
        short_deltaadjustedsharecount: item?.deltaAdjustedShareCount,
        short_deltaadjustedsharecountWithoutTrades:
          item?.deltaAdjustedShareCountWithoutTrades,
        short_ch: item?.ch,
        short_pL: parseFloat(item?.pL),
        short_pLWithoutTrades: parseFloat(item?.pLWithoutTrades),
        short_cost: parseFloat(item?.avgCost),
        short_costWithoutTrades: parseFloat(item?.avgCostWithoutTrades),
        short_last: item?.last,
        short_tickerSymbol: item?.tickerSymbol,
        short_chpercentage: parseFloat(item?.chPercetage ?? item?.chPercentage),
        short_pl_avg_cost: parseFloat(
          (item?.last - item?.avgCost) * item?.deltaAdjustedShareCount
        ),
        short_percentage_avg_cost: parseFloat(
          ((item?.pL * item?.avgCost) / item?.avgCost) * 100
        ),
        dataSource: item?.dataSource,
        fetched: item?.fetched,
        valid: item?.valid,
        short_tdg: item?.tdg,
        short_custodian: item?.custodian,
      }))
      ?.sort((a, b) => b?.short_size - a?.short_size);

    const renamedLong = long
      ?.map((item) => ({
        long_size: item?.size,
        long_sizeWithoutTrades: item?.sizeWithoutTrades,
        long_deltaadjustedsharecount: item?.deltaAdjustedShareCount,
        long_deltaadjustedsharecountWithoutTrades:
          item?.deltaAdjustedShareCountWithoutTrades,
        long_ch: item?.ch,
        long_pL: parseFloat(item?.pL),
        long_pLWithoutTrades: parseFloat(item?.pLWithoutTrades),
        long_cost: parseFloat(item?.avgCost),
        long_costWithoutTrades: parseFloat(item?.avgCostWithoutTrades),
        long_last: item?.last,
        long_current: item?.sharePrice,
        long_tickerSymbol: item?.tickerSymbol,
        long_chpercentage: parseFloat(item?.chPercetage ?? item?.chPercentage),
        long_pl_avg_cost: parseFloat(
          (item?.last - item?.avgCost) * item?.deltaAdjustedShareCount
        ),
        long_percentage_avg_cost: parseFloat(
          ((item?.pL * item?.avgCost) / item?.avgCost) * 100
        ),
        dataSource: item?.dataSource,
        fetched: item?.fetched,
        valid: item?.valid,
        long_tdg: item?.tdg,
        long_custodian: item?.custodian,
      }))
      ?.sort((a, b) => b?.long_size - a?.long_size);

    return { renamedLong, renamedShort };
  }, [data]);

  useEffect(() => {
    const { renamedLong, renamedShort } = processedData;

    setExpandLong(renamedLong);
    setExpandShort(renamedShort);

    const filteredUsersLong = applySortFilter(
      renamedLong,
      getComparator(longSortOrder, orderByLong),
      longFilterName
    );

    const filteredUsersShort = applySortFilter(
      renamedShort,
      getComparator(shortSortOrder, orderByShort),
      shortFilterName
    );

    setlong(filteredUsersLong);
    setshort(filteredUsersShort);
    setLongScrollTop(0);
    setShortScrollTop(0);
  }, [
    processedData,
    longSortOrder,
    orderByLong,
    orderByShort,
    longFilterName,
    shortSortOrder,
    shortFilterName,
  ]);

  useEffect(() => {
    onExportDataChange?.({ long, short });
  }, [long, short, onExportDataChange]);

  // Virtual scrolling calculations for long table
  const longVirtualData = useMemo(() => {
    if (!long || long.length === 0) return { visibleRows: [], totalHeight: 0 };

    const bodyHeight = getPositionTableBodyHeight(longContainerHeight);
    const startIndex = Math.max(
      0,
      Math.floor(longScrollTop / ROW_HEIGHT) - BUFFER_SIZE
    );
    const rowsInView = Math.ceil(bodyHeight / ROW_HEIGHT);
    const endIndex = Math.min(
      long.length,
      startIndex + rowsInView + BUFFER_SIZE * 2
    );

    const visibleRows = long.slice(startIndex, endIndex).map((row, index) => ({
      ...row,
      virtualIndex: startIndex + index,
    }));

    return {
      visibleRows,
      totalHeight: long.length * ROW_HEIGHT,
      startIndex,
      endIndex,
    };
  }, [long, longScrollTop, longContainerHeight]);

  // Virtual scrolling calculations for short table
  const shortVirtualData = useMemo(() => {
    if (!short || short.length === 0)
      return { visibleRows: [], totalHeight: 0 };

    const bodyHeight = getPositionTableBodyHeight(shortContainerHeight);
    const startIndex = Math.max(
      0,
      Math.floor(shortScrollTop / ROW_HEIGHT) - BUFFER_SIZE
    );
    const rowsInView = Math.ceil(bodyHeight / ROW_HEIGHT);
    const endIndex = Math.min(
      short.length,
      startIndex + rowsInView + BUFFER_SIZE * 2
    );

    const visibleRows = short.slice(startIndex, endIndex).map((row, index) => ({
      ...row,
      virtualIndex: startIndex + index,
    }));

    return {
      visibleRows,
      totalHeight: short.length * ROW_HEIGHT,
      startIndex,
      endIndex,
    };
  }, [short, shortScrollTop, shortContainerHeight]);

  // Scroll handlers
  const handleLongScroll = useCallback((event) => {
    setLongScrollTop(event.target.scrollTop);
  }, []);

  const handleShortScroll = useCallback((event) => {
    setShortScrollTop(event.target.scrollTop);
  }, []);

  const handleRequestSort = (event, property) => {
    if (property.startsWith("long_")) {
      const isAsc = longSortOrder === "asc";
      setLongSortOrder(isAsc ? "desc" : "asc");
      setOrderByLong(property);
    } else if (property.startsWith("short_")) {
      const isAsc = shortSortOrder === "asc";
      setShortSortOrder(isAsc ? "desc" : "asc");
      setOrderByShort(property);
    }
  };

  const toggleGridView = () => {
    setIsGridView(!isGridView);
  };
  const toggleGridViewShort = () => {
    setIsGridViewShort(!isGridViewShort);
  };
  const theme = useTheme();
  const TABLE_HEAD_PORTFOLIO_SHORT_SLICED = [
    ...TABLE_HEAD_PORTFOLIO_SHORT.slice(0, 8),
    ...TABLE_HEAD_PORTFOLIO_SHORT.slice(10),
  ];
  const TABLE_HEAD_PORTFOLIO_LONG_SLICED = [
    ...TABLE_HEAD_PORTFOLIO_LONG.slice(0, 8),
    ...TABLE_HEAD_PORTFOLIO_LONG.slice(10),
  ];
  return (
    <Container maxWidth="xxl" sx={{ marginLeft: "-40px", mt: 1 }}>
      <Grid container spacing={2}>
        <Grid size={{ xs: 6, md: 6 }}>
          <Box
            sx={{
              border: `1px solid ${theme.palette.lightGrey.tableHeader}`,
              borderRadius: "2px",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                background: theme.palette.green.longPositions,
                px: 1,
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{ color: theme.palette.lightGrey.textColor }}
              >
                Longs Positions ({long.length})
              </Typography>
              <IconButton
                onClick={toggleGridView}
                aria-label="Toggle GridView"
                sx={{ height: "20px", width: "20px" }}
              >
                <FullscreenIcon sx={{ height: "15px", width: "15px" }} />
              </IconButton>
            </Box>

            <TableContainer
              onScroll={handleLongScroll}
              ref={longTableRef}
              sx={{
                flex: 1,
                overflow: "auto",
                height: "calc(100vh - 250px)",
                maxHeight: "calc(100vh - 250px)",
              }}
            >
              <Table
                size="small"
                stickyHeader
                aria-label="a dense table"
                sx={{
                  borderCllapse: "collapse",
                }}
              >
                <VantageListHead
                  headLabel={TABLE_HEAD_PORTFOLIO_LONG_SLICED}
                  noStatus={true}
                  title={true}
                  order={longSortOrder}
                  orderBy={orderByLong}
                  onRequestSort={handleRequestSort}
                />

                <TableBody>
                  {/* Virtual scrolling spacer */}
                  {longVirtualData.startIndex > 0 && (
                    <TableRow
                      style={{
                        height: longVirtualData.startIndex * ROW_HEIGHT,
                      }}
                    >
                      <td colSpan={TABLE_HEAD_PORTFOLIO_LONG_SLICED.length} />
                    </TableRow>
                  )}

                  {/* Visible rows only */}
                  {longVirtualData.visibleRows?.map((row, i) => (
                    <PositionTable
                      key={`${row.long_tickerSymbol}-${row.virtualIndex}`}
                      row={row}
                      fundVal={fundVal}
                      strategyVal={StrategeyVal}
                      visible={false}
                      positionType="long"
                    />
                  ))}

                  {/* Virtual scrolling spacer */}
                  {longVirtualData.endIndex < long.length && (
                    <TableRow
                      style={{
                        height:
                          (long.length - longVirtualData.endIndex) * ROW_HEIGHT,
                      }}
                    >
                      <td colSpan={TABLE_HEAD_PORTFOLIO_LONG_SLICED.length} />
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <br />
          </Box>
        </Grid>
        <Grid size={{ xs: 6, md: 6 }}>
          <Box
            sx={{
              border: `1px solid ${theme.palette.lightGrey.tableHeader}`,
              borderRadius: "4px",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                background: theme.palette.red.shortPositions,
                px: 1,
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{ color: theme.palette.lightGrey.textColor }}
              >
                Shorts Positions ({short.length})
              </Typography>
              <IconButton
                onClick={toggleGridViewShort}
                aria-label="Toggle GridView"
                sx={{ height: "20px", width: "20px" }}
              >
                <FullscreenIcon sx={{ height: "15px", width: "15px" }} />
              </IconButton>
            </Box>

            <TableContainer
              onScroll={handleShortScroll}
              ref={shortTableRef}
              sx={{
                flex: 1,
                flexShrink: 0,
                overflow: "auto",
                height: "calc(100vh - 250px)",
                maxHeight: "calc(100vh - 250px)",
              }}
            >
              <Table
                size="small"
                stickyHeader
                aria-label="a dense table"
                sx={{
                  borderCllapse: "collapse",
                }}
              >
                <VantageListHead
                  headLabel={TABLE_HEAD_PORTFOLIO_SHORT_SLICED}
                  noStatus={true}
                  title={true}
                  order={shortSortOrder}
                  orderBy={orderByShort}
                  onRequestSort={handleRequestSort}
                />

                <TableBody>
                  {/* Virtual scrolling spacer */}
                  {shortVirtualData.startIndex > 0 && (
                    <TableRow
                      style={{
                        height: shortVirtualData.startIndex * ROW_HEIGHT,
                      }}
                    >
                      <td colSpan={TABLE_HEAD_PORTFOLIO_SHORT_SLICED.length} />
                    </TableRow>
                  )}

                  {/* Visible rows only */}
                  {shortVirtualData.visibleRows?.map((row, i) => (
                    <PositionTable
                      key={`${row.short_tickerSymbol}-${row.virtualIndex}`}
                      row={row}
                      fundVal={fundVal}
                      strategyVal={StrategeyVal}
                      visible={false}
                      positionType="short"
                    />
                  ))}

                  {/* Virtual scrolling spacer */}
                  {shortVirtualData.endIndex < short.length && (
                    <TableRow
                      style={{
                        height:
                          (short.length - shortVirtualData.endIndex) *
                          ROW_HEIGHT,
                      }}
                    >
                      <td colSpan={TABLE_HEAD_PORTFOLIO_SHORT_SLICED.length} />
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <br />
          </Box>
        </Grid>
      </Grid>
      <PositionPopUp
        open={isGridView}
        setOpen={setIsGridView}
        data={expandLong}
        positionType="long"
        title="Longs Positions"
      />
      <PositionPopUp
        open={isGridViewShort}
        setOpen={setIsGridViewShort}
        data={expandShort}
        positionType="short"
        title="Shorts Positions"
      />
    </Container>
  );
});

// Memoize the component to prevent unnecessary re-renders
export default React.memo(AssetPositions);
